import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { Skeleton } from 'primeng/skeleton';
import { Chip } from 'primeng/chip';
import {
  CompanyPackageStatsForAdminResponse,
  CompanyPackageTableResponse,
  EnumDtoOfUserPackageStatus,
  GetCompanyPackagesForAdminQuery,
  IPaginationResponseOfCompanyPackageTableResponse,
  PackagesClient,
  UserPackageStatus,
} from '../../../infrastructure/shared/model/fss.api';
import { LocationAdminService } from '../../../infrastructure/shared/services/location-admin.service';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { Arrow } from '../../../infrastructure/shared/icons/arrow/arrow';
import { Cube01 } from '../../../infrastructure/shared/icons/cube-01/cube-01';
import { CheckCircle } from '../../../infrastructure/shared/icons/check-circle/check-circle';
import { Hourglas03 } from '../../../infrastructure/shared/icons/hourglas-03/hourglas-03';
import { ClockRewind } from '../../../infrastructure/shared/icons/clock-rewind/clock-rewind';
import { Loading01 } from '../../../infrastructure/shared/icons/loading-01/loading-01';
import { formatNumberPlain } from '../../../infrastructure/shared/functions/functions';
import {
  getDateFormatedSr,
  getDateFormatedSrWithTime,
} from '../../../infrastructure/shared/services/date.service';

type SubTab = 0 | 1 | 2; // 0 = Aktivni, 1 = Istorija, 2 = U obradi

@Component({
  standalone: true,
  selector: 'app-admin-client-packages-tab',
  imports: [
    Tab,
    TabList,
    Tabs,
    TableModule,
    Skeleton,
    Chip,
    ButtonComponent,
    Arrow,
    Cube01,
    CheckCircle,
    Hourglas03,
    ClockRewind,
    Loading01,
  ],
  templateUrl: './admin-client-packages-tab.html',
  styleUrl: './admin-client-packages-tab.scss',
})
export class AdminClientPackagesTab {
  private _client = inject(PackagesClient);
  private _location = inject(LocationAdminService);
  private _cdr = inject(ChangeDetectorRef);

  clientId = input.required<string>();

  stats = signal<CompanyPackageStatsForAdminResponse | undefined>(undefined);

  packages: CompanyPackageTableResponse[] = [];
  first = 0;
  isLoading = true;
  activeTab = signal<SubTab>(0);

  paginatedData: IPaginationResponseOfCompanyPackageTableResponse = {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  } as IPaginationResponseOfCompanyPackageTableResponse;

  constructor() {
    effect(() => {
      const id = this.clientId();
      if (id) {
        this._loadStats(id);
      }
    });
  }

  onTabChange(value: any) {
    this.activeTab.set(Number(value ?? 0) as SubTab);
    this.paginatedData.currentPage = 1;
    this.load();
  }

  loadLazy(event: any) {
    this.paginatedData.pageSize = event.rows;
    this.paginatedData.currentPage = Math.floor(event.first / event.rows) + 1;
    this.load();
  }

  next() {
    this.paginatedData.currentPage!++;
    this.load();
  }

  prev() {
    this.paginatedData.currentPage!--;
    this.load();
  }

  load() {
    this.isLoading = true;
    this.packages = [];
    this._cdr.detectChanges();

    this._loadPackages().then((res) => {
      this.packages = res.result.data ?? [];
      this.paginatedData = res.result;
      this.first = (this.paginatedData.currentPage! - 1) * this.paginatedData.pageSize!;
      this.isLoading = false;
      this._cdr.markForCheck();
    });
  }

  routeToPackageDetails(id: string | undefined) {
    if (!id) return;
    this._location.routeToPackageDetails(id);
  }

  getBarPercent(p: CompanyPackageTableResponse): number {
    if (!p.totalHours) return 0;
    return Math.round(((p.remainingHours ?? 0) / p.totalHours) * 100);
  }

  isBarRed(p: CompanyPackageTableResponse): boolean {
    if (!p.totalHours) return false;
    return (p.remainingHours ?? 0) / p.totalHours <= 0.2;
  }

  getIstorijaChipLabel(p: CompanyPackageTableResponse): string {
    if (p.status?.value === UserPackageStatus.CANCELLED) {
      return p.status?.description ?? '';
    }
    if ((p.remainingHours ?? 0) === 0) return 'Iskorišćen';
    if (p.validTo && new Date(p.validTo).getTime() < Date.now()) return 'Istekao';
    return p.status?.description ?? '';
  }

  getIstorijaChipClass(p: CompanyPackageTableResponse): string {
    if (p.status?.value === UserPackageStatus.CANCELLED) return 'red sm';
    if ((p.remainingHours ?? 0) === 0) return 'gray sm';
    if (p.validTo && new Date(p.validTo).getTime() < Date.now()) return 'red sm';
    return 'gray sm';
  }

  getStatusChipClass(status: EnumDtoOfUserPackageStatus | undefined): string {
    switch (status?.value) {
      case UserPackageStatus.NEW:
        return 'info sm';
      case UserPackageStatus.INVOICE_SENT:
        return 'warn sm';
      case UserPackageStatus.ACTIVE:
      case UserPackageStatus.EXTENDED:
        return 'success sm';
      case UserPackageStatus.CANCELLED:
        return 'red sm';
      case UserPackageStatus.EXPIRED:
        return 'gray sm';
      default:
        return 'gray sm';
    }
  }

  private _statusesForActiveTab(): UserPackageStatus[] {
    switch (this.activeTab()) {
      case 0:
        return [UserPackageStatus.ACTIVE, UserPackageStatus.EXTENDED];
      case 1:
        return [UserPackageStatus.CANCELLED, UserPackageStatus.EXPIRED];
      case 2:
        return [UserPackageStatus.NEW, UserPackageStatus.INVOICE_SENT];
      default:
        return [];
    }
  }

  private async _loadStats(userId: string) {
    const res = await firstValueFrom(this._client.getCompanyPackageStatsForAdmin(userId));
    this.stats.set(res.result);
  }

  private async _loadPackages() {
    return await firstValueFrom(
      this._client.getCompanyPackagesForAdmin({
        filter: {
          userId: this.clientId(),
          statuses: this._statusesForActiveTab(),
        },
        pageNumber: this.paginatedData.currentPage,
        pageSize: this.paginatedData.pageSize,
      } as GetCompanyPackagesForAdminQuery),
    );
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly getDateFormatedSr = getDateFormatedSr;
  protected readonly getDateFormatedSrWithTime = getDateFormatedSrWithTime;
  protected readonly formatNumber = formatNumberPlain;
}
